import { type LinkProps } from 'react-router';

/**
 * Adds a `redirectTo` query parameter carrying `currentPath` to a link
 * target, so the destination can send the user back afterwards. Preserves
 * any existing query string and hash. A `To` object argument is left
 * untouched: the function returns a fresh object rather than mutating the
 * input, so a stable target reused across calls no longer accumulates
 * `redirectTo`. A target that already carries the field is appended to, not
 * deduplicated, so avoid feeding this function its own output. Backs
 * {@link usePathWithRedirect} and {@link useNavigateWithRedirect}.
 *
 * @param to The link target: a path string or a `To` object.
 * @param currentPath Path to store as the redirect target.
 * @param REDIRECT_TO_FIELD Query parameter name to use. Defaults to
 *   `redirectTo`.
 * @returns The target with the redirect parameter appended.
 */
export const getPathWithRedirect = (
  to: LinkProps['to'],
  currentPath: string,
  REDIRECT_TO_FIELD = 'redirectTo',
) => {
  if (typeof to === 'number') return to;
  const redirectTo = new URLSearchParams([[REDIRECT_TO_FIELD, currentPath]]);
  if (typeof to === 'string') {
    const hash = to.includes('#') ? to.split('#')[1] : '';
    let path = to.split('#')[0] || '';
    path = path.includes('?')
      ? `${path}&${redirectTo}`
      : `${path}?${redirectTo}`;
    return hash ? `${path}#${hash}` : path;
  }
  const search =
    typeof to.search === 'string' && to.search.includes('?')
      ? `${to.search}&${redirectTo}`
      : `?${redirectTo}`;
  return { ...to, search };
};
